import { DataItemType } from '../service/data-set/data-item-type.js'

/**
 * MIME type mappings for media types
 */
export const MEDIA_MIME_TYPES = {
  image: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/svg+xml',
    'image/tiff'
  ],
  video: [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime', // .mov
    'video/x-msvideo', // .avi
    'video/x-matroska', // .mkv
    'video/mpeg'
  ],
  audio: [
    'audio/mpeg', // .mp3
    'audio/wav',
    'audio/wave',
    'audio/x-wav',
    'audio/ogg',
    'audio/mp4', // .m4a
    'audio/aac',
    'audio/webm',
    'audio/flac'
  ]
}

/**
 * File extension to MIME type mapping
 */
export const EXTENSION_TO_MIME: Record<string, string> = {
  // Images
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  tiff: 'image/tiff',
  tif: 'image/tiff',

  // Videos
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  mpeg: 'video/mpeg',
  mpg: 'video/mpeg',

  // Audio
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  wave: 'audio/wave',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  flac: 'audio/flac'
}

/**
 * Check if a MIME type is valid for a given media type
 */
export function isValidMimeType(mediaType: DataItemType, mimetype: string): boolean {
  if (!mimetype) return false

  const allowedTypes = MEDIA_MIME_TYPES[mediaType as keyof typeof MEDIA_MIME_TYPES]
  if (!allowedTypes) return false

  return allowedTypes.some(type => mimetype.toLowerCase().startsWith(type.toLowerCase()))
}

/**
 * Check if file extension is allowed based on options.accept
 */
export function isAcceptedFormat(filename: string, acceptFormats?: string[]): boolean {
  if (!acceptFormats || acceptFormats.length === 0) return true

  const ext = filename.split('.').pop()?.toLowerCase()
  if (!ext) return false

  return acceptFormats.some(format => format.toLowerCase() === ext)
}

/**
 * Validate file size against options.maxSize
 */
export function isValidFileSize(fileSize: number, maxSize?: number): boolean {
  if (!maxSize) return true
  return fileSize <= maxSize
}

/**
 * Validation result
 */
export interface MediaValidationResult {
  valid: boolean
  errors: string[]
}

/**
 * Validate media file against DataItem options
 */
export function validateMediaFile(
  file: { mimetype: string; size?: number; name: string },
  mediaType: DataItemType,
  options?: { accept?: string[]; maxSize?: number }
): MediaValidationResult {
  const errors: string[] = []

  // Check MIME type
  if (!isValidMimeType(mediaType, file.mimetype)) {
    errors.push(
      `Invalid file type '${file.mimetype}' for ${mediaType}. Expected: ${MEDIA_MIME_TYPES[mediaType as keyof typeof MEDIA_MIME_TYPES]?.join(', ')}`
    )
  }

  // Check accepted formats (if specified in options)
  if (options?.accept && !isAcceptedFormat(file.name, options.accept)) {
    errors.push(`File format not accepted. Allowed formats: ${options.accept.join(', ')}`)
  }

  // Check file size (if specified in options)
  if (file.size && options?.maxSize && !isValidFileSize(file.size, options.maxSize)) {
    const maxSizeMB = (options.maxSize / 1024 / 1024).toFixed(2)
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2)
    errors.push(`File size (${fileSizeMB}MB) exceeds maximum allowed size (${maxSizeMB}MB)`)
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Check if a DataItemType is a media type
 */
export function isMediaType(type: DataItemType): boolean {
  return type === DataItemType.image || type === DataItemType.video || type === DataItemType.audio
}

/**
 * Check if a DataItemType is a file or media type
 */
export function isFileOrMediaType(type: DataItemType): boolean {
  return (
    type === DataItemType.file ||
    type === DataItemType.image ||
    type === DataItemType.video ||
    type === DataItemType.audio
  )
}
